import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import {
  Box,
  DoubleChevronDownIcon,
  DoubleChevroneUpIcon,
  Preloader,
  RawButton,
  Text,
  WarningIcon,
} from '@common-fe/common-fe';
import { capitalize, uniqueId } from 'lodash';
import styled from 'styled-components';

import { CommunicationChannelsType, NoteTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import ActivityLog from '@/modules/ActivityLog';
import Assistant from '@/modules/assistant';
import { useBreadcrumbs } from '@/modules/core/hooks';
import useGetOrganizationById from '@/modules/core/hooks/useGetOrganizationById';
import useHasAccess from '@/modules/core/hooks/useHasAccess';
import { useProgressScroll, useScrollStore } from '@/modules/core/hooks/useScrollSidebar';
import { SETUP_CONTAINER_UI_ID } from '@/modules/core/hooks/useScrollSidebar/useProgressScroll';
import useGetNotesQuery from '@/modules/employee/Employee/queries/useGetNotes.query';
import { useModulesCollapseStore, useMountedIdStore } from '@/modules/employee/Employee/stores';
import { AddCardDependentModal } from '@/modules/transaction/CardDetails/AddCardDependentModal/AddCardDependentModal';
import { useSearchParams } from '@/modules/transaction/Cards/hooks/useSearchParams';
import { useGetCardsQuery } from '@/modules/transaction/Cards/queries/useGetCards.query';
import theme from '@/styles/theme';
import { useScrollRestoration } from '@/utils/hooks';

import Beneficiaries from './Beneficiaries/Beneficiaries';
import useGetBeneficiariesQuery from './Beneficiaries/useGetBeneficiaries.query';
import { useHasAccessToViewBeneficiaries } from './Beneficiaries/useHasBeneficiaryAccess';
import { Communications } from './Communications/Communications';
import EmployeeLoginHistory from './EmployeeLoginHistory/EmployeeLoginHistory';
import {
  employeeConfigModules,
  MODULES,
  useEmployeeModules,
} from './EmployeeSidebar/hooks/useEmployeeModules';
import useEmployeeSubModules from './EmployeeSidebar/hooks/useEmployeeSubModules';
import { EnrollmentBalances } from './EnrollmentBalances/EnrollmentBalances.1';
import { ExpenseDetails } from './ExpenseDetails/ExpenseDetails';
import { ExpenseDetailsSubmitClaimButton } from './ExpenseDetails/ExpenseDetailsSubmitClaimButton';
import { ACTIVE_ENROLLMENT_STATUS, useGetEnrollments } from './hooks/useGetEnrollment';
import MarkBadData from './MarkBadData/MarkBadData';
import CardsBlock from './CardsBlock';
import { EmployeeCIPVerificationBanner } from './cip';
import Dependents, { DependentsAddButton } from './Dependents';
import { MyDocuments } from './Documents';
import EmployeeSave from './EmployeeSave';
import EmployeeSidebar from './EmployeeSidebar';
import EmployeeTopbar from './EmployeeTopbar';
import { EnrollmentModal } from './EnrollmentModal';
import FraudReview from './FraudReview';
import { useGetEmployeeById } from './hooks';
import ModuleWrapper from './ModuleWrapper';
import PersonalInformation from './PersonalInformation';
import RegistrationAndLogin from './RegistrationAndLogin';
import SpendTrackerBlock from './SpendTrackerBlock';

const scrollingDelay = 2000;

const FixedWrapper = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: ${() => theme.colors.canvas};
  height: max-content;
`;
const StyledWrapper = styled(Box)`
  > div:last-child {
    padding-bottom: 72px;
  }
`;

interface Props {
  isDefaultEditMode?: boolean;
}
const Employee: React.FC<Props> = () => {
  const {
    path,
    params: { id: employeeId },
  } = useRouteMatch<{ id: string }>();
  const hasAccessToViewBeneficiaries = useHasAccessToViewBeneficiaries();
  const { isHSAExists, isLoading: areEnrollmentsLoading } = useGetEnrollments({
    employeeId,
    status: ACTIVE_ENROLLMENT_STATUS,
  });
  const { isLoading: areBeneficiariesLoading } = useGetBeneficiariesQuery(
    hasAccessToViewBeneficiaries && isHSAExists && !areEnrollmentsLoading
  );
  const { hash } = useLocation();
  const [isSubmitted, setSubmitted] = useState(false);
  const [isExternalScrolled, setIsExternalScrolled] = useState(false);
  const { setMountedId, resetMountedId } = useMountedIdStore();
  const { collapseSignal, expandSignal, collapseAll, expandAll } = useModulesCollapseStore();
  const [isAllCollapsed, setIsAllCollapsed] = useState(false);
  const handleToggleCollapseAll = useCallback(() => {
    if (isAllCollapsed) {
      expandAll();
    } else {
      collapseAll();
    }
    setIsAllCollapsed(!isAllCollapsed);
  }, [isAllCollapsed, collapseAll, expandAll]);
  const { query: externalScroll } = useSearchParams('scrollToModule');
  const isEditMode = useMemo(() => path === ROUTES.EMPLOYEE_EDIT_VIEW(), [path]);
  const history = useHistory();
  const goToEditEmployeePage = useCallback(
    (isEdit: boolean) => {
      if (isEdit) {
        history.push(`${ROUTES.EMPLOYEE_EDIT_VIEW(employeeId)}${history.location.search}`);
      } else {
        history.push(`${ROUTES.EMPLOYEE_VIEW(employeeId)}${history.location.search}`);
      }
    },
    [employeeId, history]
  );
  const { isLoading, data: employeeData, isEmployeeSet } = useGetEmployeeById();

  const { formattedData, isLoading: isOverridablePropertiesLoading } = useGetOrganizationById(
    employeeData?.organizationId
  );
  const hasAccessToGetNotes = useHasAccess([
    { permission: Permissions.VIEW_ER },
    { permission: Permissions.VIEW_PROFILE },
    { permission: Permissions.PROCESS_CLAIM },
  ]);
  const { notes } = useGetNotesQuery(NoteTypes.ORG_NOTES, employeeData?.organizationId, hasAccessToGetNotes);
  const hasAccessToCreateClaim = useHasAccess([
    {
      permission: Permissions.CREATE_CLAIM,
    },
  ]);

  const hasAccessToViewTransactor = useHasAccess([
    {
      permission: Permissions.VIEW_TRANSACTOR,
    },
  ]);

  const hasSubmitClaimButton = useMemo(
    () =>
      formattedData?.overridableProperties?.isReimbursementAllowed &&
      hasAccessToCreateClaim &&
      hasAccessToViewTransactor,
    [formattedData, hasAccessToCreateClaim, hasAccessToViewTransactor]
  );
  const { hasCards } = useGetCardsQuery(employeeId);

  const isContainEmail: boolean = useMemo(
    () =>
      !!formattedData?.overridableProperties?.communicationChannels?.find(
        (elem) => elem === CommunicationChannelsType.EMAIL_SMS
      ),
    [formattedData]
  );
  const isContainNotificationCenter = useMemo(
    () =>
      !!formattedData?.overridableProperties?.communicationChannels?.find(
        (elem) => elem === CommunicationChannelsType.NOTIFICATION_CENTER
      ),
    [formattedData]
  );
  const hasActivityLog = useHasAccess([{ permission: Permissions.VIEW_PROFILE }]);
  const hasDangerZone = useHasAccess([{ permission: Permissions.HIDE_BAD_DATA }]);
  const exclude = useMemo(
    () => [
      ...(hasActivityLog ? [] : [MODULES.ACTIVITY_LOG]),
      ...(hasDangerZone ? [] : [MODULES.DANGER_ZONE]),
    ],
    [hasActivityLog, hasDangerZone]
  );
  const employeeModules = useEmployeeModules({
    hasCards,
    isContainNotificationCenter,
    isContainEmail,
    exclude,
  });
  const employeeSubModules = useEmployeeSubModules();
  const { onScrollHandler, scrollToSection } = useProgressScroll({
    ...employeeModules,
    ...employeeSubModules,
  });

  const scrollToModule = useScrollStore((state) => state.scrollToModule);
  const handleReset = useScrollStore((state) => state.handleReset);

  useScrollRestoration({
    key: `${path}:${employeeId}`,
    containerId: SETUP_CONTAINER_UI_ID,
    restore: !hash && !scrollToModule && !externalScroll,
  });

  const fullName = useMemo(
    () => `${employeeData?.firstName} ${employeeData?.lastName}`.trim(),
    [employeeData]
  );
  useBreadcrumbs(
    [
      {
        route: ROUTES.EMPLOYEES,
        title: 'Employees',
      },
      {
        route: ROUTES.EMPLOYER_SETUP,
        title: fullName,
      },
    ],
    [fullName],
    { backBtn: true }
  );

  const isHasEditAccess = useHasAccess([
    {
      permission: Permissions.EDIT_EE_DATA,
    },
    {
      permission: Permissions.EDIT_PROFILE,
    },
  ]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (scrollToModule || externalScroll) {
      timer = setTimeout(() => {
        scrollToSection({ module: scrollToModule || (externalScroll as string) });
        setIsExternalScrolled(true);
      }, 3000);
    }
    return () => {
      if (isExternalScrolled) {
        clearTimeout(timer);
        handleReset();
      }
    };
  }, [scrollToModule, scrollToSection, handleReset, isExternalScrolled, externalScroll]);

  useEffect(() => {
    setMountedId(uniqueId());
    return () => resetMountedId();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (hasCards && !isLoading && hash === `#${MODULES.CARDS}`) {
      scrollToSection({ module: MODULES.CARDS });
      timer = setTimeout(() => {
        scrollToSection({ module: MODULES.CARDS });
        window.history.replaceState(
          null,
          window.document.title,
          window.location.href.replace(`#${MODULES.CARDS}`, '')
        );
      }, scrollingDelay);
    }
    if (
      !isLoading &&
      !areBeneficiariesLoading &&
      !areEnrollmentsLoading &&
      hash === `#${MODULES.BENEFICIARIES}`
    ) {
      scrollToSection({ module: MODULES.BENEFICIARIES });
      timer = setTimeout(() => scrollToSection({ module: MODULES.BENEFICIARIES }), scrollingDelay);
    }
    return () => {
      clearTimeout(timer);
      handleReset();
    };
  }, [
    hasCards,
    scrollToSection,
    isLoading,
    hash,
    handleReset,
    areBeneficiariesLoading,
    areEnrollmentsLoading,
  ]);

  if (!isEmployeeSet || isLoading || isOverridablePropertiesLoading) {
    return <Preloader />;
  }

  return (
    <Box direction="row" width="100%">
      <EmployeeSidebar
        hasCards={hasCards}
        isContainNotificationCenter={isContainNotificationCenter}
        isContainEmail={isContainEmail}
        scrollToSection={scrollToSection}
      />
      <Box
        direction="column"
        style={{ position: 'relative' }}
        fill
        overflow="auto"
        onScroll={onScrollHandler}
        id={SETUP_CONTAINER_UI_ID}
        align="center"
      >
        <FixedWrapper width="100%" height={{ min: 'max-content' }}>
          <EmployeeTopbar
            isEditMode={isEditMode}
            hasSubmitClaimButton={hasSubmitClaimButton}
            scrollToSection={scrollToSection}
            leadingActions={<FraudReview placement="topbar" employeeName={fullName} />}
          >
            {isHasEditAccess && (
              <EmployeeSave
                isEditMode={isEditMode}
                onToggleEdit={goToEditEmployeePage}
                isSubmitted={isSubmitted}
                onToggleSubmit={setSubmitted}
              />
            )}
          </EmployeeTopbar>
        </FixedWrapper>
        <FraudReview placement="content" employeeName={fullName} />
        <Box pad={{ horizontal: '40px' }} margin={{ top: '40px' }}>
          <StyledWrapper width={theme.defaultContentWidth} data-c="ss">
            <RawButton data-testid="employee-details_collapse-all_button" onClick={handleToggleCollapseAll}>
              <Box direction="row" align="center" margin={{ bottom: 'spacing12' }}>
                <Box height="24px" align="center" justify="center" pad="7px" margin={{ right: 'spacing4' }}>
                  {isAllCollapsed ? (
                    <DoubleChevronDownIcon size="10px" />
                  ) : (
                    <DoubleChevroneUpIcon size="10px" />
                  )}
                </Box>
                <Text
                  size="12px"
                  weight={500}
                  color="textSecondary"
                  textAlign="end"
                  style={{ lineHeight: '18px' }}
                >
                  {isAllCollapsed ? 'Expand all modules' : 'Collapse all modules'}
                </Text>
              </Box>
            </RawButton>
            <ModuleWrapper
              title={employeeConfigModules[MODULES.PERSONAL_INFO]}
              anchorId={MODULES.PERSONAL_INFO}
              isDefaultExtended
            >
              <PersonalInformation
                isShowErrors={isSubmitted}
                isEditMode={isEditMode}
                notes={notes}
                isOrganizationLocked={formattedData?.isLocked}
                allowedPaymentMethodConfigs={
                  formattedData?.overridableProperties?.allowedPaymentMethodConfigs
                }
              >
                <EmployeeCIPVerificationBanner employeeId={employeeId} />
              </PersonalInformation>
            </ModuleWrapper>

            <ModuleWrapper
              title="Registration & Login"
              anchorId={MODULES.REGISTRATION_AND_LOGIN}
              isDefaultExtended
            >
              <RegistrationAndLogin isEditMode={isEditMode} isShowErrors={isSubmitted} />
            </ModuleWrapper>

            <ModuleWrapper
              anchorId={MODULES.ENROLLMENT_AND_BALANCES}
              title={employeeConfigModules[MODULES.ENROLLMENT_AND_BALANCES]}
              isDefaultExtended
              actions={<EnrollmentModal />}
              wrapperTestId="EnrollmentBalancesHeader-wrapper"
              titleTestId="CardDetailsHeader-holder-name"
            >
              <EnrollmentBalances employeeName={employeeData?.fullName}>
                <SpendTrackerBlock />
              </EnrollmentBalances>
            </ModuleWrapper>
            <ModuleWrapper
              anchorId={MODULES.EXPENSE_DETAILS}
              title={employeeConfigModules[MODULES.EXPENSE_DETAILS]}
              isDefaultExtended
              actions={hasSubmitClaimButton ? <ExpenseDetailsSubmitClaimButton /> : null}
              wrapperTestId="ExpenseDetailsHeader-wrapper"
              titleTestId="ExpenseDetailsHeader-title"
            >
              <ExpenseDetails />
            </ModuleWrapper>

            <ModuleWrapper
              anchorId={MODULES.DOCUMENTS}
              title={employeeConfigModules[MODULES.DOCUMENTS]}
              isDefaultExtended
            >
              <MyDocuments />
            </ModuleWrapper>

            {hasCards && (
              <ModuleWrapper
                anchorId={MODULES.CARDS}
                title={employeeConfigModules[MODULES.CARDS]}
                isDefaultExtended
                actions={<AddCardDependentModal />}
                wrapperTestId="CardDetailsHeader-wrapper"
                titleTestId="CardDetailsHeader-holder-name"
              >
                <CardsBlock />
              </ModuleWrapper>
            )}

            <ModuleWrapper
              anchorId={MODULES.DEPENDENTS}
              title={employeeConfigModules[MODULES.DEPENDENTS]}
              isDefaultExtended
              actions={<DependentsAddButton />}
              wrapperTestId="DependentsHeader-wrapper"
              titleTestId="DependentsHeader-title"
            >
              <Dependents />
            </ModuleWrapper>

            {hasAccessToViewBeneficiaries && isHSAExists ? (
              <ModuleWrapper
                anchorId={MODULES.BENEFICIARIES}
                title={capitalize(MODULES.BENEFICIARIES)}
                isDefaultExtended
              >
                <Beneficiaries />
              </ModuleWrapper>
            ) : null}

            {(isContainNotificationCenter || isContainEmail) && (
              <ModuleWrapper title="Communications" anchorId={MODULES.COMMUNICATIONS} isDefaultExtended>
                <Communications
                  employeeId={employeeId}
                  isContainEmail={isContainEmail}
                  isContainNotificationCenter={isContainNotificationCenter}
                  // communicationConfigChannels={employeeData?.communicationConfigChannels}
                  // communicationChannels={formattedData?.overridableProperties?.communicationChannels}
                  phoneNumber={employeeData?.homePhoneNumber}
                  email={employeeData?.employeeEmail}
                  // notificationPriority={employeeData?.notificationPriority}
                  isLoading={isLoading}
                />
              </ModuleWrapper>
            )}

            <ModuleWrapper anchorId={MODULES.LOGIN_HISTORY}>
              <EmployeeLoginHistory collapseSignal={collapseSignal} expandSignal={expandSignal} />
            </ModuleWrapper>

            {hasActivityLog ? (
              <ModuleWrapper anchorId={MODULES.ACTIVITY_LOG}>
                <ActivityLog
                  title={employeeConfigModules[MODULES.ACTIVITY_LOG]}
                  collapseSignal={collapseSignal}
                  expandSignal={expandSignal}
                />
              </ModuleWrapper>
            ) : null}

            {hasDangerZone ? (
              <ModuleWrapper
                anchorId={MODULES.DANGER_ZONE}
                title={employeeConfigModules[MODULES.DANGER_ZONE]}
                icon={<WarningIcon />}
                isDefaultExtended
                hasDivider={false}
              >
                <MarkBadData employee={employeeData} isEditMode={isEditMode} />
              </ModuleWrapper>
            ) : null}
          </StyledWrapper>
        </Box>
      </Box>

      {/* Fixed-position overlay; does not affect layout. */}
      <Assistant participantName={fullName} />
    </Box>
  );
};
export default Employee;
